import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["filters", "crossFilters", "operators", "crossOperators"],
  _excluded2 = ["filters", "crossFilters", "operators", "crossOperators"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useMemo, useCallback, useRef } from 'react';
import { useWeb5Context } from "../../../lib/Web5Context";
import { isEmptyObject } from "../../../utils/objects";
const useForceBIUtils = _ref => {
  let {
    standardFieldsSchemaMap,
    extraFieldsSchemaMap,
    rangeFields,
    crossFilterIdSeparator,
    formatDate,
    getEntitiesManager
  } = _ref;
  const {
    t,
    entities: web4Entities,
    getWeb4EntityFromString
  } = useWeb5Context();
  const isInitialized = useRef(false);
  const uiFiltersSchemaById = useRef(null);
  const uiFiltersSchemaByFieldConfiguration = useRef(null);
  const entities = useMemo(() => {
    const {
      COMPANIES,
      CONTACTS,
      ACTIVITIES,
      OPPORTUNITIES,
      SALESORDERS
    } = web4Entities;
    return [COMPANIES, CONTACTS, ACTIVITIES, OPPORTUNITIES, SALESORDERS];
  }, [web4Entities]);
  const mainEntity = useMemo(() => {
    const {
      COMPANIES
    } = web4Entities;
    return COMPANIES;
  }, [web4Entities]);
  const getMappedFiltersSchemasBy = useCallback(() => {
    let mappedFilterSchemaById = {};
    let mappedFilterSchemaByFieldConfiguration = {};
    entities.forEach(current => {
      if (!(mappedFilterSchemaById !== null && mappedFilterSchemaById !== void 0 && mappedFilterSchemaById[current.entity])) mappedFilterSchemaById[current.entity] = {};
      if (!(mappedFilterSchemaByFieldConfiguration !== null && mappedFilterSchemaByFieldConfiguration !== void 0 && mappedFilterSchemaByFieldConfiguration[current.entity])) mappedFilterSchemaByFieldConfiguration[current.entity] = {};
      const manager = getEntitiesManager(current.entity);
      const filterSchema = manager.getFilterSchema();
      filterSchema.forEach(filter => {
        mappedFilterSchemaById[current.entity][filter.id] = filter;
        if (filter.extraFilterProps) {
          mappedFilterSchemaById[current.entity][filter.extraFilterProps.id] = filter.extraFilterProps;
        }
        mappedFilterSchemaByFieldConfiguration[current.entity][filter.fieldConfiguration || filter.id] = filter;
      });
    });
    uiFiltersSchemaById.current = mappedFilterSchemaById;
    uiFiltersSchemaByFieldConfiguration.current = mappedFilterSchemaByFieldConfiguration;
    isInitialized.current = true;
  }, [entities, getEntitiesManager]);
  const init = useCallback(() => {
    if (!isInitialized.current) {
      getMappedFiltersSchemasBy();
      isInitialized.current = true;
    }
  }, [getMappedFiltersSchemasBy]);
  const getFieldDescription = useCallback(_ref2 => {
    var _uiFiltersSchemaByFie, _uiFiltersSchemaByFie2, _standardFieldsSchema, _extraFieldsSchemaMap, _fieldConfiguration;
    let {
      entity,
      fieldId
    } = _ref2;
    init();
    let fieldConfiguration = ((_uiFiltersSchemaByFie = uiFiltersSchemaByFieldConfiguration.current) === null || _uiFiltersSchemaByFie === void 0 ? void 0 : (_uiFiltersSchemaByFie2 = _uiFiltersSchemaByFie[entity]) === null || _uiFiltersSchemaByFie2 === void 0 ? void 0 : _uiFiltersSchemaByFie2[fieldId]) || null;
    if (!fieldConfiguration) fieldConfiguration = (standardFieldsSchemaMap === null || standardFieldsSchemaMap === void 0 ? void 0 : (_standardFieldsSchema = standardFieldsSchemaMap[entity]) === null || _standardFieldsSchema === void 0 ? void 0 : _standardFieldsSchema[fieldId]) || null;
    if (!fieldConfiguration) fieldConfiguration = (extraFieldsSchemaMap === null || extraFieldsSchemaMap === void 0 ? void 0 : (_extraFieldsSchemaMap = extraFieldsSchemaMap[entity]) === null || _extraFieldsSchemaMap === void 0 ? void 0 : _extraFieldsSchemaMap[fieldId]) || null;
    return ((_fieldConfiguration = fieldConfiguration) === null || _fieldConfiguration === void 0 ? void 0 : _fieldConfiguration.description) || fieldId;
  }, [extraFieldsSchemaMap, standardFieldsSchemaMap, init]);
  const getFiltersErrorMessage = useCallback(_ref3 => {
    let {
      message,
      entityFields
    } = _ref3;
    if (!message || !entityFields || !Object.keys(entityFields).length) return;
    let finalMessage = `${message}`;
    const messages = Object.entries(entityFields).reduce((arr, _ref4) => {
      let [key, fields] = _ref4;
      const entity = getWeb4EntityFromString(key);
      let message = `${t(entity.labels.plural)}:`;
      fields.forEach((fieldId, index) => {
        const description = getFieldDescription({
          entity: entity.entity,
          fieldId
        });
        if (index === fields.length - 1) {
          message = `${message} ${description}.`;
        } else {
          message = `${message} ${description},`;
        }
      });
      arr.push(message);
      return arr;
    }, []);
    if (messages.length) {
      let entitiesMessage = '';
      messages.forEach((current, index) => {
        if (index === 0) {
          entitiesMessage = `${current}`;
        } else {
          entitiesMessage = ` ${current}`;
        }
      });
      finalMessage = t(finalMessage, [entitiesMessage]);
    }
    return finalMessage;
  }, [getFieldDescription, getWeb4EntityFromString, t]);
  const getDataType = useCallback((entity, id) => {
    var _standardFieldsSchema2, _extraFieldsSchemaMap2, _uiFiltersSchemaById$, _uiFiltersSchemaById$2;
    init();
    const newEntity = entity !== null && entity !== void 0 && entity.extraFieldName ? entity : getWeb4EntityFromString(entity);
    if (!newEntity || !id) return '';
    const entityName = newEntity.extraFieldName;
    let dataType = '';
    if (standardFieldsSchemaMap !== null && standardFieldsSchemaMap !== void 0 && (_standardFieldsSchema2 = standardFieldsSchemaMap[entityName]) !== null && _standardFieldsSchema2 !== void 0 && _standardFieldsSchema2[id]) {
      dataType = standardFieldsSchemaMap[entityName][id].dataType || '';
    } else if (extraFieldsSchemaMap !== null && extraFieldsSchemaMap !== void 0 && (_extraFieldsSchemaMap2 = extraFieldsSchemaMap[entityName]) !== null && _extraFieldsSchemaMap2 !== void 0 && _extraFieldsSchemaMap2[id]) {
      dataType = extraFieldsSchemaMap[entityName][id].dataType || '';
    } else if ((_uiFiltersSchemaById$ = uiFiltersSchemaById.current) !== null && _uiFiltersSchemaById$ !== void 0 && (_uiFiltersSchemaById$2 = _uiFiltersSchemaById$[newEntity.entity]) !== null && _uiFiltersSchemaById$2 !== void 0 && _uiFiltersSchemaById$2[id]) {
      dataType = uiFiltersSchemaById.current[newEntity.entity][id].dataType || '';
    } else {
      console.error(`This field has no matching datatype: ${id}`);
    }
    return dataType;
  }, [extraFieldsSchemaMap, getWeb4EntityFromString, init, standardFieldsSchemaMap]);
  const formatCompleteValuesForWeb = useCallback(_ref5 => {
    let {
      entity,
      id,
      completeValues,
      value
    } = _ref5;
    init();
    const dataType = getDataType(entity, id);
    let newCompleteValues = completeValues;
    if (rangeFields.includes(dataType)) {
      newCompleteValues.value = [null, null];
      if (dataType === 'date') {
        newCompleteValues = [null, null];
        if (completeValues.from) newCompleteValues[0] = new Date(completeValues.from);
        if (completeValues.to) newCompleteValues[1] = new Date(completeValues.to);
      } else {
        newCompleteValues = ['', ''];
        if (completeValues.from) newCompleteValues[0] = completeValues.from;
        if (completeValues.to) newCompleteValues[1] = completeValues.to;
      }
    } else if (dataType === 'bool') {
      newCompleteValues = {};
      newCompleteValues.value = completeValues === true ? '1' : '2';
      newCompleteValues.label = completeValues === true ? t('common_yes') : t('common_no');
    }
    return newCompleteValues;
  }, [getDataType, init, t, rangeFields]);
  const endOfDay = function (date) {
    if (date === void 0) {
      date = new Date();
    }
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  };
  const formatValuesForBackend = useCallback(_ref6 => {
    let {
      entity,
      id,
      completeValues
    } = _ref6;
    init();
    const dataType = getDataType(entity, id);
    let value;
    if (rangeFields.includes(dataType)) {
      value = {
        from: null,
        to: null
      };
      if (completeValues[0]) value.from = completeValues[0];else if (completeValues.from) value.from = completeValues.from;
      if (completeValues[1]) value.to = completeValues[1];else if (completeValues.to) value.to = completeValues.to;
      if (dataType === 'date') {
        if (value.from) {
          value.from = formatDate(value.from, 'YYYY-MM-DD HH:mm:ss');
        }
        if (value.to) {
          const date = endOfDay(value.to);
          value.to = formatDate(date, 'YYYY-MM-DD HH:mm:ss');
        }
      }
    } else if (dataType === 'bool') {
      value = completeValues.value.toString() === '1' ? true : false;
    } else if (Array.isArray(completeValues)) {
      value = completeValues.map(current => current.value);
    } else if (completeValues !== null && completeValues !== void 0 && completeValues.value) {
      value = [completeValues.value.toString()];
    } else {
      value = completeValues;
    }
    return value;
  }, [formatDate, getDataType, init, rangeFields]);

  /* -->
  ** Filters structure in FrontEnd:
  {
  filters: {[those filters related to the main entity, normally accounts]},
  crossFilters: {
      opportunities: {...}
      ...rest,
  },
  operators: {[those operators related to the main entity, normally accounts]},
  crossOperators: {
      contacts: {...}
      ...rest,
  },
  }
  ** Filters structure in Backend:
  {
  filters: {
      accounts: {...},
      contacts: {...},
      opportunities: {...},
      ...rest,
  },
  operators: {
      accounts: {...},
      contacts: {...}
      ...rest,
  }
  }
  <-- */

  const translateFiltersFromBackend = useCallback(_ref7 => {
    let {
      filters: backendFilters,
      operators: backendOperators
    } = _ref7;
    init();
    let newFilters = {};
    let crossFilters = {};
    let newOperators = {};
    let crossOperators = {};
    if (!isEmptyObject(backendFilters)) {
      Object.entries(backendFilters).forEach(_ref8 => {
        let [entity, filters] = _ref8;
        if (entity === mainEntity.trueName) {
          Object.entries(filters).forEach(_ref9 => {
            let [filterId, filter] = _ref9;
            const fieldConfiguration = uiFiltersSchemaByFieldConfiguration.current[mainEntity.entity][filterId];
            const id = (fieldConfiguration === null || fieldConfiguration === void 0 ? void 0 : fieldConfiguration.id) || filterId;
            newFilters[id] = {
              completeValues: formatCompleteValuesForWeb({
                entity: mainEntity,
                id,
                completeValues: filter.completeValues,
                value: filter.value
              }),
              id
            };
          });
        } else {
          const crossEntity = getWeb4EntityFromString(entity);
          if (!crossFilters[crossEntity.entity]) crossFilters[crossEntity.entity] = {};
          Object.entries(filters).forEach(_ref10 => {
            let [filterId, filter] = _ref10;
            const fieldConfiguration = uiFiltersSchemaByFieldConfiguration.current[crossEntity.entity][filterId];
            const id = (fieldConfiguration === null || fieldConfiguration === void 0 ? void 0 : fieldConfiguration.id) || filterId;
            crossFilters[crossEntity.entity][id] = {
              completeValues: formatCompleteValuesForWeb({
                entity: crossEntity,
                id,
                completeValues: filter.completeValues,
                value: filter.value
              }),
              id
            };
          });
        }
      });
    }
    if (!isEmptyObject(backendOperators)) {
      Object.entries(backendOperators).forEach(_ref11 => {
        let [entity, operators] = _ref11;
        if (entity === mainEntity.trueName) {
          Object.entries(operators).forEach(_ref12 => {
            let [operatorId, operator] = _ref12;
            const fieldConfiguration = uiFiltersSchemaByFieldConfiguration.current[mainEntity.entity][operatorId];
            const id = (fieldConfiguration === null || fieldConfiguration === void 0 ? void 0 : fieldConfiguration.id) || operatorId;
            newOperators[id] = {
              value: operator.value,
              extra: fieldConfiguration !== null && fieldConfiguration !== void 0 && fieldConfiguration.id ? false : true,
              label: operator.label
            };
          });
        } else {
          const crossEntity = getWeb4EntityFromString(entity);
          if (!crossOperators[crossEntity.entity]) crossOperators[crossEntity.entity] = {};
          Object.entries(operators).forEach(_ref13 => {
            let [operatorId, operator] = _ref13;
            const fieldConfiguration = uiFiltersSchemaByFieldConfiguration.current[crossEntity.entity][operatorId];
            const id = (fieldConfiguration === null || fieldConfiguration === void 0 ? void 0 : fieldConfiguration.id) || operatorId;
            crossOperators[crossEntity.entity][id] = {
              value: operator.value,
              extra: fieldConfiguration !== null && fieldConfiguration !== void 0 && fieldConfiguration.id ? false : true,
              label: operator.label
            };
          });
        }
      });
    }
    return {
      filters: newFilters,
      operators: newOperators,
      crossFilters,
      crossOperators
    };
  }, [formatCompleteValuesForWeb, init, mainEntity, getWeb4EntityFromString]);
  const formatCustomFilterValuesForBackend = useCallback(filters => {
    if (!Object.keys(filters).length) return {};
    return Object.entries(filters).reduce((obj, _ref14) => {
      let [key, value] = _ref14;
      if (!(value !== null && value !== void 0 && value.completeValues)) return obj;
      const completeValues = value.completeValues;
      let finalValue = [];
      if (Array.isArray(completeValues)) {
        finalValue = completeValues.map(current => current.value);
      } else {
        finalValue = [completeValues.value];
      }
      obj[key] = {
        value: finalValue
      };
      return obj;
    }, {});
  }, []);
  const translateFiltersForBackend = useCallback(_ref15 => {
    let {
      filters,
      crossFilters,
      operators,
      crossOperators,
      customFilters
    } = _ref15;
    init();
    let newFilters = {};
    if (!isEmptyObject(filters)) {
      newFilters[mainEntity.trueName] = Object.entries(filters).reduce((obj, _ref16) => {
        let [filterId, filter] = _ref16;
        const filterConfig = uiFiltersSchemaById.current[mainEntity.entity][filterId];
        const id = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.fieldConfiguration) || (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.id) || filterId;
        const dataType = getDataType(mainEntity, id);
        if (dataType) {
          obj[id] = {
            value: formatValuesForBackend({
              entity: mainEntity,
              id,
              completeValues: filter.completeValues
            }),
            dataType
          };
        }
        return obj;
      }, {});
    }
    if (!isEmptyObject(crossFilters)) {
      Object.entries(crossFilters).forEach(_ref17 => {
        let [entity, filters] = _ref17;
        const newEntity = entity !== null && entity !== void 0 && entity.extraFieldName ? entity : getWeb4EntityFromString(entity);
        Object.entries(filters).forEach(_ref18 => {
          let [filterId, filter] = _ref18;
          const filterConfig = uiFiltersSchemaById.current[newEntity.entity][filterId];
          const id = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.fieldConfiguration) || (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.id) || filterId;
          const dataType = getDataType(newEntity, id);
          if (dataType) {
            if (!newFilters[newEntity.trueName]) {
              newFilters[newEntity.trueName] = {};
            }
            newFilters[newEntity.trueName] = _objectSpread(_objectSpread({}, newFilters[newEntity.trueName]), {}, {
              [id]: {
                value: formatValuesForBackend({
                  entity: newEntity,
                  id,
                  completeValues: filter.completeValues
                }),
                dataType
              }
            });
          }
        });
      });
    }
    if (customFilters && Object.keys(customFilters).length > 0) {
      Object.entries(customFilters).forEach(_ref19 => {
        let [entity, filters] = _ref19;
        const customs = formatCustomFilterValuesForBackend(filters);
        newFilters[entity] = _objectSpread(_objectSpread({}, newFilters[entity]), customs);
      });
    }
    let newOperators = {};
    if (!isEmptyObject(operators)) {
      newOperators[mainEntity.trueName] = Object.entries(operators).reduce((obj, _ref20) => {
        let [operatorId, operator] = _ref20;
        const filterConfig = uiFiltersSchemaById.current[mainEntity.entity][operatorId];
        const id = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.fieldConfiguration) || (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.id) || operatorId;
        if (!id) return obj;
        obj[id] = {
          value: operator.value
        };
        return obj;
      }, {});
    }
    if (!isEmptyObject(crossOperators)) {
      Object.entries(crossOperators).forEach((_ref21, index) => {
        let [entity, operators] = _ref21;
        const newEntity = entity !== null && entity !== void 0 && entity.extraFieldName ? entity : getWeb4EntityFromString(entity);
        Object.entries(operators).forEach(_ref22 => {
          let [operatorId, operator] = _ref22;
          if (!newOperators[newEntity.trueName]) {
            newOperators[newEntity.trueName] = {};
          }
          const filterConfig = uiFiltersSchemaById.current[newEntity.entity][operatorId];
          const id = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.fieldConfiguration) || (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.id) || operatorId;
          newOperators[newEntity.trueName] = _objectSpread(_objectSpread({}, newOperators[newEntity.trueName]), {}, {
            [id]: {
              value: operator.value
            }
          });
        });
      });
    }
    return {
      filters: newFilters,
      operators: newOperators
    };
  }, [formatValuesForBackend, formatCustomFilterValuesForBackend, getDataType, getWeb4EntityFromString, init, mainEntity]);

  // If dashboard filters or operators are applied, they will override the widget filters and operators.
  const overrideDashboardFilters = useCallback(_ref23 => {
    let {
      dashboardFilters,
      widgetFilters
    } = _ref23;
    const {
      filters: dFilters,
      operators: dOperators
    } = dashboardFilters;
    const {
      filters: wFilters,
      operators: wOperators
    } = widgetFilters;
    const filtersObj = entities.reduce((obj, entity) => {
      const trueName = entity.trueName;
      if (!(obj !== null && obj !== void 0 && obj.filters)) obj.filters = {};
      if (!(obj !== null && obj !== void 0 && obj.operators)) obj.operators = {};
      if (wFilters !== null && wFilters !== void 0 && wFilters[trueName] || dFilters !== null && dFilters !== void 0 && dFilters[trueName]) {
        const entityFilters = {};
        if (Object.keys((dFilters === null || dFilters === void 0 ? void 0 : dFilters[trueName]) || {}).length > 0) {
          _extends(entityFilters, dFilters[trueName] || {});
        }

        // Add widget filters, but only if its ID does not exist in dashboard filters or operators
        if (Object.keys((wFilters === null || wFilters === void 0 ? void 0 : wFilters[trueName]) || {}).length > 0) {
          Object.entries(wFilters[trueName]).forEach(_ref24 => {
            var _dOperators$trueName, _dFilters$trueName;
            let [filterId, filter] = _ref24;
            const dOperatorExists = dOperators === null || dOperators === void 0 ? void 0 : (_dOperators$trueName = dOperators[trueName]) === null || _dOperators$trueName === void 0 ? void 0 : _dOperators$trueName[filterId];
            const dFilterExists = dFilters === null || dFilters === void 0 ? void 0 : (_dFilters$trueName = dFilters[trueName]) === null || _dFilters$trueName === void 0 ? void 0 : _dFilters$trueName[filterId];
            if (!dOperatorExists && !dFilterExists) {
              entityFilters[filterId] = filter;
            }
          });
        }
        if (Object.keys(entityFilters).length > 0) {
          obj.filters[trueName] = entityFilters;
        }
      }
      if (wOperators !== null && wOperators !== void 0 && wOperators[trueName] || dOperators !== null && dOperators !== void 0 && dOperators[trueName]) {
        const entityOperators = {};
        if (Object.keys((dOperators === null || dOperators === void 0 ? void 0 : dOperators[trueName]) || {}).length > 0) {
          _extends(entityOperators, dOperators[trueName] || {});
        }

        // Add widget operators, but only if its ID does not exist in dashboard filters or operators
        if (Object.keys((wOperators === null || wOperators === void 0 ? void 0 : wOperators[trueName]) || {}).length > 0) {
          Object.entries(wOperators[trueName]).forEach(_ref25 => {
            var _dFilters$trueName2, _dOperators$trueName2;
            let [operatorId, operator] = _ref25;
            const dFilterExists = dFilters === null || dFilters === void 0 ? void 0 : (_dFilters$trueName2 = dFilters[trueName]) === null || _dFilters$trueName2 === void 0 ? void 0 : _dFilters$trueName2[operatorId];
            const dOperatorExists = dOperators === null || dOperators === void 0 ? void 0 : (_dOperators$trueName2 = dOperators[trueName]) === null || _dOperators$trueName2 === void 0 ? void 0 : _dOperators$trueName2[operatorId];
            if (!dFilterExists && !dOperatorExists) {
              entityOperators[operatorId] = operator;
            }
          });
        }
        if (Object.keys(entityOperators).length > 0) {
          obj.operators[trueName] = entityOperators;
        }
      }
      return obj;
    }, {});
    return filtersObj;
  }, [entities]);
  const processDataToSendFromWidget = useCallback(_ref26 => {
    let {
      data,
      dashboardFilters,
      customFilters
    } = _ref26;
    let dFilters = {};
    let dOperators = {};
    const translated = translateFiltersForBackend(_objectSpread(_objectSpread({}, dashboardFilters), {}, {
      customFilters
    }));
    dFilters = translated.filters;
    dOperators = translated.operators;
    const {
        filters,
        crossFilters,
        operators,
        crossOperators
      } = data,
      restData = _objectWithoutProperties(data, _excluded);
    const {
      filters: wFilters,
      operators: wOperators
    } = translateFiltersForBackend({
      filters,
      crossFilters,
      operators,
      crossOperators
    });
    const finalDashboardFilters = {
      filters: dFilters,
      operators: dOperators
    };
    const widgetFilters = {
      filters: wFilters,
      operators: wOperators
    };
    const newFilters = overrideDashboardFilters({
      dashboardFilters: finalDashboardFilters,
      widgetFilters
    });
    const newData = _objectSpread(_objectSpread({}, restData), {}, {
      filters: newFilters.filters,
      operators: newFilters.operators
    });
    return newData;
  }, [overrideDashboardFilters, translateFiltersForBackend]);
  const processDataToSendFromCrud = useCallback(_ref27 => {
    let {
      data
    } = _ref27;
    const {
        filters: dataFilters,
        crossFilters: dataCrossFilters,
        operators: dataOperators,
        crossOperators: dataCrossOperators
      } = data,
      restData = _objectWithoutProperties(data, _excluded2);
    let filtersObj = {
      filters: dataFilters,
      crossFilters: dataCrossFilters,
      operators: dataOperators,
      crossOperators: dataCrossOperators
    };
    const {
      filters: newFilters,
      operators: newOperators
    } = translateFiltersForBackend(_objectSpread({}, filtersObj));
    return _objectSpread(_objectSpread({}, restData), {}, {
      filters: newFilters,
      operators: newOperators
    });
  }, [translateFiltersForBackend]);

  // This method returns unique filters (joining filters and operators)
  const getUniqueFilters = useCallback(_ref28 => {
    let {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = _ref28;
    init();
    let uiFilters = [];
    let uiFilterNames = [];
    if (!isEmptyObject(filters)) {
      Object.entries(filters).forEach(_ref29 => {
        var _uiFiltersSchemaById$3, _uiFiltersSchemaById$4, _fieldConfiguration2;
        let [filterId, filter] = _ref29;
        let fieldConfiguration = (_uiFiltersSchemaById$3 = uiFiltersSchemaById.current) === null || _uiFiltersSchemaById$3 === void 0 ? void 0 : (_uiFiltersSchemaById$4 = _uiFiltersSchemaById$3[mainEntity.entity]) === null || _uiFiltersSchemaById$4 === void 0 ? void 0 : _uiFiltersSchemaById$4[filterId];
        if (!fieldConfiguration) {
          var _extraFieldsSchemaMap3;
          fieldConfiguration = extraFieldsSchemaMap === null || extraFieldsSchemaMap === void 0 ? void 0 : (_extraFieldsSchemaMap3 = extraFieldsSchemaMap[mainEntity.extraFieldName]) === null || _extraFieldsSchemaMap3 === void 0 ? void 0 : _extraFieldsSchemaMap3[filterId];
        }
        if (!fieldConfiguration) {
          console.error(`This filter has not matching fieldConfiguration: ${filterId}`);
          return;
        }
        const id = filterId;
        uiFilters.push({
          entityLabel: mainEntity.labels.plural,
          id,
          fullId: id,
          description: ((_fieldConfiguration2 = fieldConfiguration) === null || _fieldConfiguration2 === void 0 ? void 0 : _fieldConfiguration2.description) || t(fieldConfiguration.label)
        });
        uiFilterNames.push(id);
      });
    }
    if (!isEmptyObject(crossFilters)) {
      Object.entries(crossFilters).forEach(_ref30 => {
        let [entity, newFilters] = _ref30;
        if (Object.keys(newFilters).length) {
          Object.entries(newFilters).forEach(_ref31 => {
            var _uiFiltersSchemaById$5, _uiFiltersSchemaById$6, _fieldConfiguration3, _fieldConfiguration4;
            let [filterId, filter] = _ref31;
            const filterEntity = getWeb4EntityFromString(entity);
            let fieldConfiguration = (_uiFiltersSchemaById$5 = uiFiltersSchemaById.current) === null || _uiFiltersSchemaById$5 === void 0 ? void 0 : (_uiFiltersSchemaById$6 = _uiFiltersSchemaById$5[filterEntity.entity]) === null || _uiFiltersSchemaById$6 === void 0 ? void 0 : _uiFiltersSchemaById$6[filterId];
            if (!fieldConfiguration) {
              var _extraFieldsSchemaMap4;
              fieldConfiguration = extraFieldsSchemaMap === null || extraFieldsSchemaMap === void 0 ? void 0 : (_extraFieldsSchemaMap4 = extraFieldsSchemaMap[filterEntity.extraFieldName]) === null || _extraFieldsSchemaMap4 === void 0 ? void 0 : _extraFieldsSchemaMap4[filterId];
            }
            if (!fieldConfiguration) {
              console.error(`This crossFilter has not matching fieldConfiguration: ${filterId}`);
              return;
            }
            const id = filterId;
            const fullUiId = `${filterEntity.entity}${crossFilterIdSeparator}${id}`;
            uiFilters.push({
              entityLabel: filterEntity.labels.plural,
              id,
              fullId: fullUiId,
              description: ((_fieldConfiguration3 = fieldConfiguration) === null || _fieldConfiguration3 === void 0 ? void 0 : _fieldConfiguration3.description) || t((_fieldConfiguration4 = fieldConfiguration) === null || _fieldConfiguration4 === void 0 ? void 0 : _fieldConfiguration4.label)
            });
            uiFilterNames.push(fullUiId);
          });
        }
      });
    }
    if (!isEmptyObject(operators)) {
      Object.entries(operators).forEach(_ref32 => {
        var _uiFiltersSchemaById$7, _uiFiltersSchemaById$8;
        let [operatorId, operator] = _ref32;
        let fieldConfiguration = (_uiFiltersSchemaById$7 = uiFiltersSchemaById.current) === null || _uiFiltersSchemaById$7 === void 0 ? void 0 : (_uiFiltersSchemaById$8 = _uiFiltersSchemaById$7[mainEntity.entity]) === null || _uiFiltersSchemaById$8 === void 0 ? void 0 : _uiFiltersSchemaById$8[operatorId];
        if (!fieldConfiguration) {
          var _extraFieldsSchemaMap5;
          fieldConfiguration = extraFieldsSchemaMap === null || extraFieldsSchemaMap === void 0 ? void 0 : (_extraFieldsSchemaMap5 = extraFieldsSchemaMap[mainEntity.extraFieldName]) === null || _extraFieldsSchemaMap5 === void 0 ? void 0 : _extraFieldsSchemaMap5[operatorId];
        }
        if (!fieldConfiguration) {
          console.error(`This operator has not matching fieldConfiguration: ${operatorId}`);
          return;
        }
        const id = operatorId;
        if (!uiFilterNames.includes(id)) {
          var _fieldConfiguration5;
          uiFilters.push({
            entityLabel: mainEntity.labels.plural,
            id,
            fullId: id,
            description: ((_fieldConfiguration5 = fieldConfiguration) === null || _fieldConfiguration5 === void 0 ? void 0 : _fieldConfiguration5.description) || t(fieldConfiguration.label)
          });
          uiFilterNames.push(id);
        }
      });
    }
    if (!isEmptyObject(crossOperators)) {
      Object.entries(crossOperators).forEach(_ref33 => {
        let [entity, newOperators] = _ref33;
        if (Object.keys(newOperators).length) {
          Object.entries(newOperators).forEach(_ref34 => {
            var _uiFiltersSchemaById$9, _uiFiltersSchemaById$10;
            let [operatorId, operator] = _ref34;
            const operatorEntity = getWeb4EntityFromString(entity);
            let fieldConfiguration = (_uiFiltersSchemaById$9 = uiFiltersSchemaById.current) === null || _uiFiltersSchemaById$9 === void 0 ? void 0 : (_uiFiltersSchemaById$10 = _uiFiltersSchemaById$9[operatorEntity.entity]) === null || _uiFiltersSchemaById$10 === void 0 ? void 0 : _uiFiltersSchemaById$10[operatorId];
            if (!fieldConfiguration) {
              var _extraFieldsSchemaMap6;
              fieldConfiguration = extraFieldsSchemaMap === null || extraFieldsSchemaMap === void 0 ? void 0 : (_extraFieldsSchemaMap6 = extraFieldsSchemaMap[operatorEntity.extraFieldName]) === null || _extraFieldsSchemaMap6 === void 0 ? void 0 : _extraFieldsSchemaMap6[operatorId];
            }
            if (!fieldConfiguration) {
              console.error(`This crossOperator has not matching field configuration: ${operatorId}`);
              return;
            }
            const id = operatorId;
            const fullUiId = `${operatorEntity.entity}${crossFilterIdSeparator}${id}`;
            if (!uiFilterNames.includes(fullUiId)) {
              var _fieldConfiguration6;
              uiFilters.push({
                entityLabel: operatorEntity.labels.plural,
                id,
                fullId: fullUiId,
                description: ((_fieldConfiguration6 = fieldConfiguration) === null || _fieldConfiguration6 === void 0 ? void 0 : _fieldConfiguration6.description) || t(fieldConfiguration.label)
              });
              uiFilterNames.push(fullUiId);
            }
          });
        }
      });
    }
    return {
      uiFilters,
      uiFilterNames
    };
  }, [crossFilterIdSeparator, extraFieldsSchemaMap, getWeb4EntityFromString, init, mainEntity, t]);
  const processFiltersToInitDashboard = useCallback(_ref35 => {
    let {
      filters: dFilters,
      operators: dOperators
    } = _ref35;
    const {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = translateFiltersFromBackend({
      filters: dFilters,
      operators: dOperators
    });
    return {
      filters,
      crossFilters,
      operators,
      crossOperators
    };
  }, [translateFiltersFromBackend]);
  const processFiltersToInitCrud = useCallback(_ref36 => {
    var _uniqueFilters$uiFilt;
    let {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = _ref36;
    const uniqueFilters = getUniqueFilters({
      filters,
      crossFilters,
      operators,
      crossOperators
    });
    let visibleFilters = [];
    let openFilters = {};
    if ((_uniqueFilters$uiFilt = uniqueFilters.uiFilters) !== null && _uniqueFilters$uiFilt !== void 0 && _uniqueFilters$uiFilt.length) {
      visibleFilters = uniqueFilters.uiFilters.map(current => {
        openFilters[current.fullId] = true;
        return current.fullId;
      });
    }
    return {
      filters,
      crossFilters,
      operators,
      crossOperators,
      visibleFilters,
      openFilters
    };
  }, [getUniqueFilters]);
  return {
    processFiltersToInitDashboard,
    processFiltersToInitCrud,
    processDataToSendFromWidget,
    processDataToSendFromCrud,
    translateFiltersForBackend,
    translateFiltersFromBackend,
    getUniqueFilters,
    entities,
    entity: mainEntity,
    getFiltersErrorMessage
  };
};
export default useForceBIUtils;